import { useMemo, useState } from 'react';

/**
 * Liste de sélection avec recherche et cases à cocher.
 *
 * Remplace les DataGrid utilisés dans les formulaires pour choisir
 * des habilitations, services, etc.
 *
 * Props :
 *   items      : tableau d'éléments { id, label, group? }
 *   selected   : tableau d'ids sélectionnés
 *   onChange   : callback(ids sélectionnés)
 *   searchable : afficher la barre de recherche (défaut : true)
 *   height     : hauteur max en px (défaut : 320)
 *   emptyText  : texte si aucun élément
 */
export interface PickerItem {
    id: string;
    label: string;
    group?: string;
}

const PickerList = (props: {
    items: PickerItem[];
    selected: string[];
    onChange: (ids: string[]) => void;
    searchable?: boolean;
    height?: number;
    emptyText?: string;
}) => {
    const { items, selected, onChange, searchable = true, height = 320, emptyText = 'Aucun élément' } = props;
    const [query, setQuery] = useState('');

    const filtered = useMemo(() => {
        if (!query.trim()) return items;
        const q = query.toLowerCase();
        return items.filter((it) => it.label.toLowerCase().includes(q) || it.id.toLowerCase().includes(q));
    }, [items, query]);

    const toggle = (id: string) => {
        if (selected.includes(id)) {
            onChange(selected.filter((x) => x !== id));
        } else {
            onChange([...selected, id]);
        }
    };

    const allChecked = filtered.length > 0 && filtered.every((it) => selected.includes(it.id));
    const someChecked = filtered.some((it) => selected.includes(it.id)) && !allChecked;

    const toggleAll = () => {
        if (allChecked) {
            onChange(selected.filter((id) => !filtered.some((it) => it.id === id)));
        } else {
            const newSel = new Set(selected);
            filtered.forEach((it) => newSel.add(it.id));
            onChange(Array.from(newSel));
        }
    };

    // Regroupement optionnel par `group`
    const groups = useMemo(() => {
        const map = new Map<string, PickerItem[]>();
        filtered.forEach((it) => {
            const key = it.group || '';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(it);
        });
        return Array.from(map.entries());
    }, [filtered]);

    return (
        <div className="eb-picker" style={{ maxHeight: height }}>
            {searchable && (
                <div className="eb-picker-search">
                    <input
                        type="text"
                        placeholder="Rechercher…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    {filtered.length > 0 && (
                        <label className="eb-picker-checkall">
                            <input
                                type="checkbox"
                                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                checked={allChecked}
                                onChange={toggleAll}
                            />
                            <span>Tout {allChecked ? 'décocher' : 'cocher'}</span>
                        </label>
                    )}
                </div>
            )}
            <div className="eb-picker-list">
                {filtered.length === 0 && <div className="eb-picker-empty">{emptyText}</div>}
                {groups.map(([group, list]) => (
                    <div key={group} className="eb-picker-group">
                        {group && <div className="eb-picker-group-label">{group}</div>}
                        {list.map((it) => (
                            <label key={it.id} className="eb-picker-item">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(it.id)}
                                    onChange={() => toggle(it.id)}
                                />
                                <span className="eb-picker-item-id">{it.id}</span>
                                <span className="eb-picker-item-label">{it.label}</span>
                            </label>
                        ))}
                    </div>
                ))}
            </div>
            <div className="eb-picker-footer">
                {selected.length} sélectionné{selected.length > 1 ? 's' : ''} sur {items.length}
            </div>
        </div>
    );
};

export default PickerList;
